import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import NoteCard from '../components/NoteCard';
import { allNotes } from '../data';

// --- Home Component Logic ---
const featuredTopics = ['c','c++','python','java','html', 'css','javascript','react', 'nodejs','docker','git','github','dsa'];

// Get unique representative notes for each topic using seriesKey
const featuredNotes = featuredTopics.map(topic => {
    const representativeNote = allNotes.find(note => 
        note.seriesKey && note.seriesKey.toLowerCase() === topic.toLowerCase()
    );
    
    if (representativeNote) {
        return {
            ...representativeNote,
            isFeatured: true,
            displayTopic: topic
        };
    }
    return null;
}).filter(Boolean);

// Remove duplicates based on seriesKey
const uniqueFeaturedNotes = featuredNotes.filter((note, index, self) => 
    index === self.findIndex(n => n.seriesKey === note.seriesKey)
);

const Home = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [exactSearch, setExactSearch] = useState('');

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setExactSearch(''); // Reset exact search when typing
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && searchTerm.trim()) {
            setExactSearch(searchTerm.trim());
        }
    };

    // DUAL SEARCH BEHAVIOR
    const filteredNotes = useMemo(() => {
        // If exact search (pressed Enter), show only exact matches
        if (exactSearch) {
            const lowerCaseExact = exactSearch.toLowerCase();
            return allNotes.filter(note => 
                note.seriesKey?.toLowerCase() === lowerCaseExact
            );
        }
        
        // If typing (no Enter), show topics starting with search term
        if (searchTerm.trim()) {
            const lowerCaseQuery = searchTerm.toLowerCase().trim();
            return allNotes.filter(note => 
                note.seriesKey?.toLowerCase().startsWith(lowerCaseQuery)
            );
        }

        return [];
    }, [searchTerm, exactSearch]);

    const isTypingSearch = searchTerm.trim() && !exactSearch;
    const isExactSearch = exactSearch.trim() !== '';
    const isSearching = isTypingSearch || isExactSearch;

    const contentToDisplay = isSearching ? filteredNotes : uniqueFeaturedNotes;

    const clearSearch = () => {
        setSearchTerm('');
        setExactSearch('');
    };

    return (
        <>
            <Helmet>
                <title>SearchTec Notes | Clean, Concise Coding Documentation</title>
                <meta name="description" content="SearchTec Notes is your essential resource for clean, concise, and up-to-date coding documentation on React, JavaScript, Node.js, HTML, CSS, and more. Master web development quickly." />
                <link rel="canonical" href="https://searchtec.in/" />
                <meta property="og:title" content="SearchTec Notes | Clean, Concise Coding Documentation" />
                <meta property="og:type" content="website" />
            </Helmet>

            {/* Hide sidebar globally */}
            <style>
                {`
                    /* Hide sidebar on home page */
                    .sidebar, 
                    [class*="sidebar"], 
                    [class*="Sidebar"],
                    aside {
                        display: none !important;
                    }
                    
                    /* Ensure main content takes full width */
                    .main-content,
                    [class*="main"],
                    [class*="content"]:not(.note-content) {
                        width: 100% !important;
                        margin-left: 0 !important;
                        margin-right: 0 !important;
                    }
                    
                    /* Adjust layout for full width */
                    .container,
                    [class*="container"] {
                        max-width: 100% !important;
                        padding-left: 1rem !important;
                        padding-right: 1rem !important;
                    }
                `}
            </style>

            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
                {/* Enhanced Hero Section */}
                <section className="text-center py-8 sm:py-12 lg:py-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-sm mb-6 sm:mb-8 lg:mb-12 mx-2 sm:mx-0 border border-blue-200">
                    <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-800 mb-3 sm:mb-4 leading-tight">
                            Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">SearchTec Notes</span> 
                        </h1>
                        
                        <p className="text-base sm:text-lg lg:text-xl text-gray-700 max-w-2xl lg:max-w-3xl mx-auto leading-relaxed px-2">
                            Master programming with clean, concise coding documentation.
                        </p>
                        
                        {/* Dual Behavior Search Bar */}
                        <div className="mt-6 sm:mt-8 max-w-md mx-auto px-2 sm:px-0">
                            <div className="relative">
                                <input
                                    type="search"
                                    placeholder="Type to browse or press Enter to search exact..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onKeyPress={handleKeyPress}
                                    className="w-full p-3 sm:p-4 pl-10 sm:pl-12 border-2 border-blue-200 rounded-xl sm:rounded-2xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm sm:text-base bg-white/95 placeholder-gray-500"
                                />
                                <div className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-blue-500">
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                
                                {/* Clear Button */}
                                {(searchTerm || exactSearch) && (
                                    <button
                                        onClick={clearSearch}
                                        className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                            
                            {/* Search Help Text */}
                            <div className="mt-2 sm:mt-3">
                                <p className="text-xs text-blue-600 font-medium">
                                    {isExactSearch ? (
                                        `✅ Exact match: "${exactSearch}"`
                                    ) : isTypingSearch ? (
                                        `🔍 Browsing topics starting with "${searchTerm}" - Press Enter for exact match`
                                    ) : (
                                        `💡 Type to browse topics or press Enter for exact search`
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
                
                {/* Enhanced Content Section */}
                <section className="mt-6 sm:mt-8 lg:mt-12 px-2 sm:px-0">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-3 sm:gap-0">
                        <div className="flex-1">
                            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">
                                {isExactSearch ? (
                                    <span className="flex items-center flex-wrap gap-2">
                                        <span className="text-green-500">✅</span>
                                        Exact Match: "{exactSearch}"
                                    </span>
                                ) : isTypingSearch ? (
                                    <span className="flex items-center flex-wrap gap-2">
                                        <span className="text-blue-500">🔍</span>
                                        Topics Starting With "{searchTerm}"
                                    </span>
                                ) : (
                                    <span className="flex items-center">
                                        <span className="text-blue-500 mr-2 sm:mr-3">📚</span>
                                        Learning Paths
                                    </span>
                                )}
                            </h2>
                            <p className="text-gray-600 text-sm sm:text-base">
                                {isExactSearch 
                                    ? `Showing exact "${exactSearch}" content`
                                    : isTypingSearch 
                                    ? `Found ${filteredNotes.length} matching topics`
                                    : `${uniqueFeaturedNotes.length} programming topics to explore`
                                }
                            </p>
                        </div>
                        
                        {!isSearching && (
                            <div className="hidden sm:flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 px-3 sm:px-4 py-2 rounded-full border border-blue-200">
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                <span>{uniqueFeaturedNotes.length} topics</span>
                            </div>
                        )}
                    </div>
                    
                    {/* Conditional Rendering */}
                    {isSearching && filteredNotes.length === 0 ? (
                        <div className="text-center py-12 sm:py-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl sm:rounded-2xl border-2 border-dashed border-blue-200 mx-2 sm:mx-0">
                            <div className="text-4xl sm:text-5xl mb-4 text-blue-300">🔍</div>
                            <p className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                                {isExactSearch 
                                    ? `No exact match found for "${exactSearch}"`
                                    : `No topics starting with "${searchTerm}"`
                                }
                            </p>
                            <p className="text-gray-600 text-sm sm:text-base max-w-md mx-auto mb-6 px-4">
                                Try different search terms
                            </p>
                            <button 
                                onClick={clearSearch}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors font-medium text-sm sm:text-base"
                            >
                                View All Topics
                            </button>
                        </div>
                    ) : (
                        // Responsive Grid Layout
                        <div className={`
                            grid gap-4 sm:gap-5 lg:gap-6 xl:gap-8
                            ${isSearching 
                                ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' 
                                : 'grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
                            }
                            px-1 sm:px-0
                        `}>
                            {contentToDisplay.map(note => (
                                <NoteCard 
                                    key={`${note.id}-${note.seriesKey}-${isSearching ? 'search' : 'featured'}`}
                                    note={note}
                                    linkTo={isSearching ? `/${note.seriesKey}/${note.id}` : `/${note.seriesKey}`}
                                    isSearchResult={isSearching}
                                /> 
                            ))}
                        </div>
                    )}

                    {/* Clear Search CTA */}
                    {isSearching && filteredNotes.length > 0 && (
                        <div className="text-center mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-blue-200">
                            <button 
                                onClick={clearSearch}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg transition-colors font-medium text-sm sm:text-base"
                            >
                                View All Topics
                            </button>
                        </div>
                    )}
                </section>
            </div>
        </>
    );
};

export default Home;
